import { Config } from 'config/envConfig';
import { useEffect, useState } from 'react';
import 'react-reflex/styles.css';
import { ReflexContainer, ReflexElement, ReflexSplitter } from 'react-reflex';
import { faUsersGear, faWrench } from '@fortawesome/free-solid-svg-icons';
import Navbar from '../navbar';
import { CollapsedSideBar } from '../collapsedSideBar';
import AgentToolSideBar from './agentToolSideBar';
import AgentMainView from './agentMainView';
import ToolsMainView from '../toolsSection/ToolsMainView';
import { alert, AlertType } from '@quyva/shared-ui';

import { getUrl } from '../pdfDisplay/utils.js';
import axios from 'axios';
const minWindowSize = 50;
const ITEMS_PER_PAGE = 7;

const SIDE_VIEWS = {
    AGENTS: 'agents',
    TOOLS: 'tools',
};

export const AgentView = ({
    newNotification,
    setNewNotification,
    viewNotifications,
    setViewNotifications,
    messages,
    socket,
    isSideViewExpanded,
    setIsSideViewExpanded,
    planName,
}) => {
    const user = JSON.parse(localStorage.getItem('current')) || {
        email: 'demo@user.com',
        token: 'demo-token',
    };
    const [activeSideView, setActiveSideView] = useState(SIDE_VIEWS.AGENTS);
    const [allTools, setAllTools] = useState([]);
    const [selectedToolId, setSelectedToolId] = useState('all');
    const [showAddInstanceDialog, setShowAddInstanceDialog] = useState(false);
    const [activeTool, setActiveTool] = useState(null); // The specific tool object to pre-fill the dialog
    const [isSmallScreen, setIsSmallScreen] = useState(false);
    const [agentViewKey, setAgentViewKey] = useState(0);
    const [agentList, setAgentList] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [loadingText, setLoadingText] = useState('');

    useEffect(() => {
        const handleResize = () => setIsSmallScreen(window.innerWidth <= 1024);
        handleResize();
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Fetch base tools
    useEffect(() => {
        const getBaseTools = async () => {
            try {
                const response = await axios.get(
                    getUrl(
                        `http://${Config.FAST_API_HOST}:${Config.FAST_API_PORT}/tool_manager/get_tools`
                    )
                );

                if (response.data) {
                    setAllTools(response.data || []);
                }
            } catch (err) {
                alert(AlertType.ERROR, 'Failed to fetch base tools.');
                console.error('Error fetching base tools:', err);
            }
        };
        getBaseTools();
    }, []);

    const updateAgentInList = (agentId, updates) => {
        setAgentList(prev =>
            prev.map(agent =>
                agent.id === agentId ? { ...agent, ...updates } : agent
            )
        );
    };

    const getAgentList = async () => {
        try {
            setIsLoading(true);
            setLoadingText('Loading Agents...');
            const dynamicUrl = getUrl(
                `http://${Config.FAST_API_HOST}:${Config.FAST_API_PORT}/agent_manager/get_user_agents/${user.email}`
            );
            const encodedToken = encodeURIComponent(user.token);
            const response = await axios.get(dynamicUrl, {
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: encodedToken,
                },
            });
            const sorted = (response.data || []).sort(
                (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
            );
            setAgentList(sorted);
        } catch (error) {
            alert(AlertType.ERROR, 'Failed to fetch agents.');
            setAgentList([]);
        } finally {
            setIsLoading(false);
            setLoadingText('');
        }
    };

    useEffect(() => {
        (async () => {
            await getAgentList();
        })();
    }, []);

    const handleAddInstance = (tool) => {
        setActiveTool(tool); // Set the tool that triggered the add
        setShowAddInstanceDialog(true);
    };
    const rerenderAgentView = () => {
        setAgentViewKey((prevKey) => prevKey + 1);
    };

    const handleSetActiveSideView = async (view) => {
        setActiveSideView(view);
        if (view === SIDE_VIEWS.AGENTS) {
            await getAgentList();
        }
    };

    return (
        <div className='z-0 w-[99vw] h-[99vh] m-auto pt-2'>
            <ReflexContainer orientation='vertical'>
                <ReflexElement
                    className='left-pane mr-0.5'
                    minSize={isSideViewExpanded ? 250 : minWindowSize}
                    size={isSideViewExpanded ? 350 : minWindowSize}
                    maxSize={isSideViewExpanded ? 700 : minWindowSize}
                >
                    <div className='pane-content w-full h-full bg-transparent'>
                        {isSideViewExpanded ? (
                            <div className='w-full h-full border border-2 overflow-auto bg-backgrounds-slate rounded-xl p-2 z-10'>
                                <AgentToolSideBar
                                    setIsSideViewExpanded={
                                        setIsSideViewExpanded
                                    }
                                    planName={planName}
                                    activeView={activeSideView}
                                    setActiveView={handleSetActiveSideView}
                                    tools={allTools}
                                    selectedToolId={selectedToolId}
                                    onSelectTool={setSelectedToolId}
                                    user={user}
                                    onAddInstance={handleAddInstance}
                                    agentList={agentList}
                                    getAgentList={getAgentList}
                                />
                            </div>
                        ) : (
                            <CollapsedSideBar
                                icon={
                                    activeSideView === SIDE_VIEWS.AGENTS
                                        ? faUsersGear
                                        : faWrench
                                } // Dynamic icon
                                title={
                                    activeSideView === SIDE_VIEWS.AGENTS
                                        ? 'Agents'
                                        : 'Tools'
                                }
                                setIsRepoViewExpanded={setIsSideViewExpanded}
                            />
                        )}
                    </div>
                </ReflexElement>
                <ReflexSplitter
                    style={{
                        border: 0,
                        background: 'white',
                        width: '6px',
                        zIndex: 10,
                        height: '98vh',
                    }}
                />
                <ReflexElement className='right-pane ml-0.5'>
                    <div className='pane-content w-full h-full bg-transparent'>
                        <div className='w-full h-full border border-2 overflow-auto bg-backgrounds-slate rounded-xl z-0'>
                            <ReflexContainer orientation='horizontal'>
                                <ReflexElement
                                    className='header'
                                    style={{
                                        minHeight: '4rem',
                                        height: '4rem',
                                        maxHeight: '4rem',
                                    }}
                                >
                                    <div className='pane-content w-full h-full p-2'>
                                        <Navbar
                                            newNotification={newNotification}
                                            setNewNotification={
                                                setNewNotification
                                            }
                                            viewNotifications={
                                                viewNotifications
                                            }
                                            setViewNotifications={
                                                setViewNotifications
                                            }
                                            messages={messages}
                                            socket={socket}
                                            setIsRepoViewExpanded={
                                                setIsSideViewExpanded
                                            }
                                        />
                                    </div>
                                </ReflexElement>
                                <ReflexElement flex={1}>
                                    <div className='pane-content h-full w-full p-2'>
                                        {activeSideView === SIDE_VIEWS.AGENTS ? (
                                            <AgentMainView
                                                key={agentViewKey}
                                                user={user}
                                                itemsPerPage={ITEMS_PER_PAGE}
                                                agentList={agentList}
                                                getAgentList={getAgentList}
                                                updateAgentInList={updateAgentInList}
                                            />
                                        ) : (
                                            <ToolsMainView
                                                user={user}
                                                allTools={allTools}
                                                selectedToolId={selectedToolId}
                                                setSelectedToolId={
                                                    setSelectedToolId
                                                }
                                                showAddInstanceDialog={
                                                    showAddInstanceDialog
                                                }
                                                setShowAddInstanceDialog={
                                                    setShowAddInstanceDialog
                                                }
                                                activeTool={activeTool}
                                            />
                                        )}
                                    </div>
                                </ReflexElement>
                            </ReflexContainer>
                        </div>
                    </div>
                </ReflexElement>
            </ReflexContainer>
        </div>
    );
};
